import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useCurrentUser, userKeys } from '../features/users/users.api';
import { apiRequest, jsonBody } from '../lib/api-client';
import type { CurrentUser } from '../lib/api-types';
import { ThemeContext, type Theme } from './theme-context';

function getInitialTheme(): Theme {
  const savedTheme = localStorage.getItem('financego-theme');
  if (savedTheme === 'light' || savedTheme === 'dark') {
    return savedTheme;
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches === true ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  useEffect(() => {
    const darkMode = currentUser.data?.preferences?.darkMode;
    if (darkMode !== undefined) setTheme(darkMode ? 'dark' : 'light');
  }, [currentUser.data?.preferences?.darkMode]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem('financego-theme', theme);

    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    themeColor?.setAttribute('content', theme === 'dark' ? '#160f24' : '#f8f6ff');
  }, [theme]);

  const saveTheme = useCallback(
    async (nextTheme: Theme) => {
      const previous = theme;
      setTheme(nextTheme);
      const user = currentUser.data;
      if (!user?.preferences) return;
      try {
        const response = await apiRequest<{ user: CurrentUser }>('/api/users/preferences', {
          body: jsonBody({ ...user.preferences, darkMode: nextTheme === 'dark' }),
          method: 'POST',
        });
        queryClient.setQueryData(userKeys.current, response.user);
      } catch (error) {
        setTheme(previous);
        throw error;
      }
    },
    [currentUser.data, queryClient, theme],
  );

  const toggleTheme = useCallback(() => {
    void saveTheme(theme === 'dark' ? 'light' : 'dark').catch(() => undefined);
  }, [saveTheme, theme]);

  const value = useMemo(
    () => ({ setTheme: saveTheme, theme, toggleTheme }),
    [saveTheme, theme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
